import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema, SortOrder, paginationSchema, } from './helpers';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
import { getExtensionPlatformIds, getUniqueExtensionEditors, } from './platform-registry';
import { ColumnDataTypes, DATE_RANGE_OPERATORS, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
import { filterOptionItemSchema } from './mcp';
import { severitySchema } from './shared/severity';
import { extensionCapabilitySchema, scoreContributorSchema } from './analyses';
/**
 * Extension source type (which IDE reported this extension).
 * Derived from registry platforms where `hasExtensions` is true.
 */
export const extensionSourceSchema = z.enum(getExtensionPlatformIds());
/**
 * Extension ID (e.g., "microsoft.python")
 */
export const extensionIdSchema = z.string().min(1);
/**
 * Highest-severity capability-detection summary for inventory rows (KIR-3781).
 * `null` = no findings. `score` is null when the winning finding has no aggregate score.
 */
export const extensionSecuritySummarySchema = z
    .object({
    severity: severitySchema,
    score: z.number().int().min(0).nullable(),
})
    .nullable();
/**
 * AgentMesh's verdict for an extension version, in AgentMesh's own buckets
 * (KIR-5208): critical → dangerous, high/medium → risky, low/clean and a
 * partial scan → clean. No bucket (null) reads "No verdict yet".
 */
export const AGENTMESH_RISK_BUCKETS = ['dangerous', 'risky', 'clean'];
export const agentmeshRiskBucketSchema = z.enum(AGENTMESH_RISK_BUCKETS);
/**
 * How much of an extension AgentMesh has scanned (KIR-5208), separate from
 * the verdict. For one version: a full scan, a partial scan (part of the code
 * read), no result, or a manual review (an AgentMesh admin set the verdict,
 * AgentMesh KIR-5273). For an extension: every installed version manually
 * reviewed, every one fully scanned or reviewed, some with any result, or
 * none. Order is AgentMesh's own.
 */
export const AGENTMESH_SCAN_COVERAGES = [
    'scanned',
    'partially_scanned',
    'unscanned',
    'manual_review',
];
export const agentmeshScanCoverageSchema = z.enum(AGENTMESH_SCAN_COVERAGES);
/**
 * Filter values for states that carry no AgentMesh verdict (KIR-4726,
 * KIR-5208). Kirin-namespaced so they cannot collide with AgentMesh's
 * vocabulary.
 */
export const RISK_ESTIMATE_STATE_FILTER_VALUES = {
    /** Risk filter: no AgentMesh verdict yet ("No verdict yet"); not built-ins. */
    NO_VERDICT: 'kirin:no-verdict',
    /**
     * @deprecated Risk filter value before KIR-5208 ("Not assessed"). Still
     * accepted from saved URLs; reads as NO_VERDICT.
     */
    NOT_ASSESSED: 'kirin:not-assessed',
    /**
     * Risk and Scan status filters: "Not applicable", a built-in extension with
     * no AgentMesh verdict. It ships with the IDE (KIR-4830).
     */
    NOT_SCANNED_BUILT_IN: 'kirin:built-in',
};
/** Scan status filter values: the coverages plus "Not applicable". */
export const SCAN_STATUS_FILTER_VALUES = [
    ...AGENTMESH_SCAN_COVERAGES,
    RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_SCANNED_BUILT_IN,
];
/**
 * AgentMesh risk estimate for inventory extension rows (KIR-1928).
 * `null` = no AgentMesh record for this extension (or feature disabled).
 *
 * `status` / `level` carry AgentMesh's own vocabulary as raw strings (e.g.
 * status 'risky' | 'unscanned', level 'medium') — deliberately NOT enums so
 * new AgentMesh values flow through without a contract change.
 */
export const extensionRiskEstimateSchema = z
    .object({
    status: z.string(),
    level: z.string().nullable(),
    reasons: z.array(z.string()),
    evidence: z.array(z.object({ snippet: z.string(), filePath: z.string() })),
    checkedAt: z.string().datetime(),
    /** Link to the AgentMesh console record; null when unknown. */
    agentmeshUrl: z.string().nullable(),
    /**
     * Marketplace AgentMesh scanned this result from (raw AgentMesh value,
     * e.g. 'vscode' / 'openvsx'). When AgentMesh holds the version in several
     * marketplaces, this is the one the Risk and Scan status come from (worst
     * verdict, then the fuller scan; KIR-5198). Null/omitted when unknown
     * (additive KIR-5361).
     */
    marketplace: z.string().nullable().optional(),
    /**
     * The verdict bucket; null = no verdict yet (KIR-5208). AgentMesh's own
     * riskVerdict when it sent one, else derived from its riskStatus. Optional
     * so responses from before KIR-5208 still parse.
     */
    riskBucket: agentmeshRiskBucketSchema.nullable().optional(),
})
    .nullable();
/**
 * Extension version detail - for nested view
 */
export const extensionVersionSchema = z.object({
    version: z.string(),
    userCount: z.number().int().min(0),
    firstSeenAt: z.string().datetime(),
    /**
     * Link to the AgentMesh console record for THIS version; null/omitted when
     * AgentMesh has no record for it (additive KIR-1928).
     */
    agentmeshUrl: z.string().nullable().optional(),
    /**
     * AgentMesh assessment for THIS exact version; null/omitted when AgentMesh
     * has no record for it (additive KIR-1928). The list item's top-level
     * riskEstimate is the worst-across-versions rollup.
     */
    riskEstimate: extensionRiskEstimateSchema.optional(),
    /**
     * Capability-detection summary for this version; null/omitted = no findings.
     * Optional so older clients/fixtures remain valid (additive KIR-3781).
     */
    securitySummary: extensionSecuritySummarySchema.optional(),
    /**
     * Scan that owns the preferred findings set for this version (same selection
     * as GET …/scan-result — installed-extensions preferred). Not necessarily
     * chronologically latest across all scans.
     */
    preferredScanId: z.string().uuid().nullable().optional(),
    /**
     * Every install of this version is from an IDE that ships it (KIR-4726).
     * Informational: Scan status and Risk follow the extension's isBuiltIn
     * (KIR-4830). Omitted when unknown.
     */
    isBuiltIn: z.boolean().optional(),
    /**
     * AgentMesh scan coverage of THIS version (KIR-5208); null = "Not
     * applicable" (a built-in version without an AgentMesh verdict) or the
     * feature is off.
     */
    scanStatus: agentmeshScanCoverageSchema.nullable().optional(),
});
/**
 * Extension list item - returned from list endpoint
 */
export const extensionListItemSchema = z.object({
    extensionId: extensionIdSchema, // e.g., "microsoft.python"
    publisher: z.string(),
    extensionName: z.string(),
    displayName: z.string().nullable(),
    versions: z.array(extensionVersionSchema),
    sources: z.array(extensionSourceSchema), // All unique sources (cursor, copilot, vscode)
    userCount: z.number().int().min(0), // Number of users with this extension (across all versions)
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(), // Most recent time any install of this extension was seen
    isVerified: z.boolean(), // Whether publisher is verified (in TRUSTED_SOURCES)
    isBuiltIn: z.boolean(), // Whether extension is built-in for any detected source
    /**
     * Capability-detection summary for this extension; null/omitted = no findings.
     * Optional so older clients/fixtures remain valid (additive KIR-3781).
     */
    securitySummary: extensionSecuritySummarySchema.optional(),
    /**
     * Scan that owns the preferred findings set for this extension (same
     * selection as GET …/scan-result). Not chronologically-latest across all scans.
     */
    preferredScanId: z.string().uuid().nullable().optional(),
    /**
     * AgentMesh risk estimate; null/omitted = not assessed (additive KIR-1928).
     */
    riskEstimate: extensionRiskEstimateSchema.optional(),
    /**
     * AgentMesh scan coverage across the installed versions (KIR-5208):
     * scanned = every version has a full scan, partially_scanned = some result
     * but not all full, unscanned = no result. Null = "Not applicable" (only
     * built-in versions without a verdict) or the feature is off.
     */
    scanStatus: agentmeshScanCoverageSchema.nullable().optional(),
});
/**
 * Extension list response with pagination
 */
export const extensionListResponseSchema = z.object({
    extensions: z.array(extensionListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
/**
 * Extension inventory request - for upsert endpoint
 */
export const extensionInventoryItemSchema = z.object({
    id: z.string(), // Extension ID (e.g., "microsoft.python")
    publisher: z.string(),
    version: z.string(),
    displayName: z.string(),
    extensionName: z.string(),
});
export const extensionInventoryRequestSchema = z.object({
    extensions: z.array(extensionInventoryItemSchema),
    source: extensionSourceSchema,
});
/**
 * Extension inventory upsert response
 */
export const extensionInventoryResponseSchema = z.object({
    success: z.boolean(),
});
/**
 * Extension inventory delete request body
 */
export const extensionInventoryDeleteRequestSchema = z.object({
    publisher: z.string().min(1),
    version: z.string().min(1),
});
/**
 * Extension inventory delete response
 */
export const extensionInventoryDeleteResponseSchema = extensionInventoryResponseSchema;
/**
 * Sort field options for extension list
 */
export const extensionSortFieldSchema = z.enum([
    'name',
    'publisher',
    'firstSeenAt',
    'userCount',
]);
/**
 * Sort order - re-exported from helpers for convenience.
 * Use the generic sortOrderSchema for new code.
 */
export const extensionSortOrderSchema = sortOrderSchema;
/**
 * Shared filter schema for query list filtering.
 * Used consistently across frontend and backend to avoid type drift.
 *
 * Format: { field: 'source', condition: 'include', values: ['built-in', 'added'] }
 *
 * Note: Values can be sent as comma-separated string in query params and will be
 * automatically parsed into an array.
 */
export const queryListFilterSchema = z.object({
    field: z.string(),
    condition: z.enum(['include', 'exclude']),
    values: z
        .union([
        z.array(z.string()),
        z.string().transform(val => val.split(',').map(v => v.trim())),
    ])
        .pipe(z.array(z.string())),
});
/**
 * Query schema for listing extensions.
 * Extends the base list query with extension-specific sort fields and filters.
 *
 * Note: Fastify's default query parser doesn't handle nested arrays like filters[0][field]=source.
 * This schema uses preprocess to manually parse the filters from the raw query object.
 */
export const extensionListQuerySchema = baseListQuerySchema
    .extend({
    sortBy: extensionSortFieldSchema.default('userCount'),
    sortOrder: sortOrderSchema.default(SortOrder.Desc),
})
    .catchall(z.any()) // Allow extra fields for manual parsing
    .transform(data => {
    // Extract filters manually from query params like: filters[0][field]=source
    const filters = [];
    const filterIndices = new Set();
    // Find all filter indices
    for (const key in data) {
        const match = key.match(/^filters\[(\d+)\]/);
        if (match) {
            filterIndices.add(parseInt(match[1], 10));
        }
    }
    // Build filter objects from the indexed params
    for (const index of Array.from(filterIndices).sort()) {
        const field = data[`filters[${index}][field]`];
        const condition = data[`filters[${index}][condition]`];
        const valuesRaw = data[`filters[${index}][values]`];
        if (field && condition && valuesRaw) {
            // Validate condition before casting to prevent malformed values
            if (condition !== 'include' && condition !== 'exclude') {
                continue; // Skip invalid filter
            }
            // Values can be comma-separated string or array
            const values = (typeof valuesRaw === 'string'
                ? valuesRaw.split(',').map(v => v.trim())
                : Array.isArray(valuesRaw)
                    ? valuesRaw
                    : [valuesRaw]).filter(v => v.length > 0); // Filter out empty strings
            filters.push({
                field,
                condition: condition,
                values,
            });
        }
    }
    return {
        page: data.page,
        limit: data.limit,
        search: data.search,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
        filters: filters.length > 0 ? filters : undefined,
    };
});
/**
 * Query parameters for listing extensions.
 * @deprecated Use extensionListQuerySchema instead for route schemas.
 * This schema uses optional fields without defaults for API client usage.
 */
export const listExtensionsParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    /** Sort field (default: 'name') */
    sortBy: extensionSortFieldSchema.optional(),
    /** Sort order (default: 'asc') */
    sortOrder: extensionSortOrderSchema.optional(),
    /** Filters array for table filtering */
    filters: z.array(queryListFilterSchema).optional(),
});
/**
 * Extension user - for drill-down endpoint showing users with a specific extension
 */
export const extensionUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    sources: z.array(extensionSourceSchema),
    version: z.string(), // Current installed version
    lastSeenAt: z.string().datetime(),
});
/**
 * Extension users response - for drill-down endpoint
 */
export const extensionUsersResponseSchema = z.object({
    extensionId: extensionIdSchema,
    users: z.array(extensionUserSchema),
});
/**
 * KIR-5281: optional narrowing of the users drill-down to one version row of
 * the expanded list. `publisher` and `version` are sent together or not at all.
 */
export const extensionUsersQuerySchema = z
    .object({
    publisher: z.string().min(1).optional(),
    version: z.string().min(1).optional(),
})
    .refine(q => (q.publisher === undefined) === (q.version === undefined), {
    message: 'publisher and version must be sent together',
});
/**
 * One security finding on the extension scan-result detail (KIR-3781 Part B).
 */
export const extensionScanResultFindingSchema = z.object({
    severity: severitySchema,
    type: z.string(),
    reason: z.string(),
    score: z.number().int().min(0).optional(),
    scoreContributors: z.array(scoreContributorSchema).optional(),
});
/**
 * Query params for GET .../extensions/:extensionId/scan-result
 */
export const extensionScanResultQuerySchema = z.object({
    version: z.string().min(1).optional(),
});
/**
 * Scan-result detail for inventory drill-down (findings + capabilities, same scan).
 */
export const extensionScanResultDetailSchema = z.object({
    extensionId: extensionIdSchema,
    version: z.string().nullable().optional(),
    publisher: z.string(),
    isVerified: z.boolean(),
    publisherTrusted: z.boolean(),
    sources: z.array(extensionSourceSchema).optional(),
    findings: z.array(extensionScanResultFindingSchema),
    capabilities: z.array(extensionCapabilitySchema),
    scanId: z.string().uuid().nullable(),
    scannedAt: z.string().datetime().nullable(),
});
/**
 * Extension editor type (which code editor the extension is installed in).
 * Derived from unique `extensionEditor` values in the platform registry.
 */
export const extensionEditorSchema = z.enum(getUniqueExtensionEditors());
/**
 * Extension severity level for risk assessment
 */
export const extensionSeveritySchema = z.enum([
    'low',
    'medium',
    'high',
    'critical',
]);
/**
 * Extension info - submitted by agent for evaluation
 */
export const extensionInfoSchema = z.object({
    id: z.string(),
    name: z.string(),
    publisher: z.string(),
    path: z.string(),
    manifestContent: z.string(),
    editor: extensionEditorSchema,
});
/**
 * Evaluate extensions request - sent by agent to backend
 */
export const evaluateExtensionsRequestSchema = z.object({
    extensions: z.array(extensionInfoSchema),
    timestamp: z.number(),
    clientVersion: z.string(),
});
/**
 * Risky extension removal source - indicates why the extension is flagged for removal
 */
export const riskyExtensionSourceSchema = z.enum([
    'vulnerability',
    'access-control',
]);
/**
 * Access control block source - indicates which rule blocked the extension.
 * The backend only produces 'blocked-by-policy'. The legacy values are kept
 * temporarily so older extension clients that validate against this enum
 * don't break on the narrowed response. Remove once all clients are updated.
 */
export const accessControlBlockSourceSchema = z.enum([
    'blocked-by-policy',
    'blocked-extensions',
    'blocked-publishers',
    'allowed-extensions',
    'allowed-publishers',
    'trusted-extensions',
    'default-behavior',
]);
/**
 * Risky extension - returned by backend when an extension exceeds severity threshold
 */
export const riskyExtensionSchema = z.object({
    id: z.string(),
    name: z.string(),
    publisher: z.string(),
    version: z.string(), // Extension version (extracted from manifest)
    path: z.string(),
    editor: extensionEditorSchema,
    severity: extensionSeveritySchema,
    reason: z.string(),
    source: riskyExtensionSourceSchema, // Why it's flagged: vulnerability or access-control
    blockSource: accessControlBlockSourceSchema.optional(), // If access-control, which rule blocked it
    allowed: z.boolean().optional(), // If true, entity matched an allow-list entry; frontend hides alert
    // KIR-4664: the backend `finding_key`(s) of the alert(s) this extension raised
    // (one per sub-policy finding). Lets the agent's removal/monitor notification
    // deep-link to the EXACT alert(s) — /{org}/alerts/{finding_key} for one, a
    // persisted alert-set for several — instead of the heuristic filtered list.
    // Optional: older backends omit it, so the agent falls back as before.
    findingKeys: z.array(z.string()).optional(),
});
/**
 * Evaluate extensions response - returned to agent
 */
export const evaluateExtensionsResponseSchema = z.object({
    riskyExtensions: z.array(riskyExtensionSchema),
    evaluatedExtensionIds: z.array(z.string()).optional(), // IDs of extensions that were successfully evaluated (not skipped/failed)
    // KIR-4324: detections suppressed by the allow list (not removed), so the agent can log them.
    allowedExtensions: z.array(riskyExtensionSchema).optional(),
});
// ============================================================================
// Organization Extensions DataTable Schemas
// ============================================================================
/**
 * Sort field options for the org extensions DataTable.
 * Includes userCount since org extensions aggregate across users.
 * Also includes 'source' (built-in/added) and 'ide' for DataTable sorting.
 */
export const orgExtensionSortFieldSchema = z.enum([
    'name',
    'publisher',
    'firstSeenAt',
    'lastSeenAt',
    'source',
    'ide',
    'userCount',
    'security',
    'riskEstimate',
    'scanStatus',
]);
/**
 * Filterable fields for the org extensions DataTable.
 * Includes USER field for filtering by users within the organization.
 */
export const ORG_EXTENSION_FILTERABLE_FIELDS = {
    NAME: 'name',
    SOURCE: 'source',
    PUBLISHER: 'publisher',
    IDE: 'ide',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
    USER: 'user',
    SECURITY: 'security',
    RISK_ESTIMATE: 'riskEstimate',
    SCAN_STATUS: 'scanStatus',
};
/**
 * Searchable fields for the org extensions DataTable.
 * Full-text search queries against these columns.
 */
export const ORG_EXTENSION_SEARCHABLE_FIELDS = {
    DISPLAY_NAME: 'displayName',
    PUBLISHER: 'publisher',
    IDE: 'ide',
    SOURCE: 'source',
    USERS: 'users',
};
/**
 * Sort fields for the org extensions DataTable.
 * Mirrors orgExtensionSortFieldSchema but as a const object for runtime use.
 */
export const ORG_EXTENSION_SORT_FIELDS = {
    NAME: 'name',
    PUBLISHER: 'publisher',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
    SOURCE: 'source',
    IDE: 'ide',
    USER_COUNT: 'userCount',
    SECURITY: 'security',
    RISK_ESTIMATE: 'riskEstimate',
    SCAN_STATUS: 'scanStatus',
};
/**
 * Column data types for org extension DataTable fields.
 * Used by createTableQuerySchema to validate operator × datatype combinations.
 */
export const ORG_EXTENSION_COLUMN_DATA_TYPES = {
    [ORG_EXTENSION_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [ORG_EXTENSION_FILTERABLE_FIELDS.SOURCE]: ColumnDataTypes.ENUM,
    [ORG_EXTENSION_FILTERABLE_FIELDS.PUBLISHER]: ColumnDataTypes.STRING,
    [ORG_EXTENSION_FILTERABLE_FIELDS.IDE]: ColumnDataTypes.ENUM,
    [ORG_EXTENSION_FILTERABLE_FIELDS.FIRST_SEEN_AT]: ColumnDataTypes.DATE,
    [ORG_EXTENSION_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
    [ORG_EXTENSION_FILTERABLE_FIELDS.USER]: ColumnDataTypes.ENUM,
    [ORG_EXTENSION_FILTERABLE_FIELDS.SECURITY]: ColumnDataTypes.ENUM,
    [ORG_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE]: ColumnDataTypes.ENUM,
    [ORG_EXTENSION_FILTERABLE_FIELDS.SCAN_STATUS]: ColumnDataTypes.ENUM,
};
/** firstSeenAt/lastSeenAt allow range operators plus the value-less operators (no equals/notEquals); applied to both org-extension DataTable schemas. */
export const ORG_EXTENSION_OPERATOR_OVERRIDES = {
    [ORG_EXTENSION_FILTERABLE_FIELDS.FIRST_SEEN_AT]: DATE_RANGE_OPERATORS,
    [ORG_EXTENSION_FILTERABLE_FIELDS.LAST_SEEN_AT]: DATE_RANGE_OPERATORS,
};
/**
 * Valid values for the source column filter (same as personal).
 */
export const orgExtensionSourceFilterValueSchema = z.enum([
    'built-in',
    'added',
]);
/**
 * Valid values for the IDE column filter.
 */
export const orgExtensionIdeFilterValueSchema = extensionSourceSchema;
/**
 * Shared refinement function to validate org extension filter values.
 * Enforces per-column operator overrides and validates source/IDE filter values.
 *
 * Use with `.superRefine()` on any schema that includes org extension filters.
 *
 * @param filters - Array of filter values (can be undefined)
 * @param ctx - Zod refinement context for adding issues
 */
export function validateOrgExtensionFilters(filters, ctx) {
    const overrides = ORG_EXTENSION_OPERATOR_OVERRIDES;
    filters?.forEach((filter, index) => {
        const override = overrides[filter.field];
        if (override && !override.includes(filter.operator)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['filters', index, 'operator'],
                message: `Operator "${filter.operator}" is not allowed for field "${filter.field}". Allowed operators: ${override.join(', ')}.`,
            });
        }
    });
    const sourceFilter = filters?.find(f => f.field === ORG_EXTENSION_FILTERABLE_FIELDS.SOURCE);
    if (sourceFilter) {
        for (const val of sourceFilter.value) {
            if (!orgExtensionSourceFilterValueSchema.safeParse(val).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['filters'],
                    message: `Invalid source filter value: "${val}". Must be one of: built-in, added`,
                });
            }
        }
    }
    const ideFilter = filters?.find(f => f.field === ORG_EXTENSION_FILTERABLE_FIELDS.IDE);
    if (ideFilter) {
        for (const val of ideFilter.value) {
            if (!orgExtensionIdeFilterValueSchema.safeParse(val).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['filters'],
                    message: `Invalid IDE filter value: "${val}". Must be one of: ${extensionSourceSchema.options.join(', ')}`,
                });
            }
        }
    }
    const securityFilter = filters?.find(f => f.field === ORG_EXTENSION_FILTERABLE_FIELDS.SECURITY);
    if (securityFilter) {
        for (const val of securityFilter.value) {
            if (!severitySchema.safeParse(val).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['filters'],
                    message: `Invalid security filter value: "${val}". Must be one of: ${severitySchema.options.join(', ')}`,
                });
            }
        }
    }
    validateScanStatusFilterValues(filters?.find(f => f.field === ORG_EXTENSION_FILTERABLE_FIELDS.SCAN_STATUS), ctx);
}
/**
 * Scan status filter values are a closed set (KIR-5208). Risk values are not
 * validated: saved URLs from before KIR-5208 carry raw AgentMesh labels,
 * which the backend maps onto their bucket.
 */
export function validateScanStatusFilterValues(filter, ctx) {
    const allowed = SCAN_STATUS_FILTER_VALUES;
    for (const val of filter?.value ?? []) {
        if (!allowed.includes(val)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['filters'],
                message: `Invalid scan status filter value: "${val}". Must be one of: ${allowed.join(', ')}`,
            });
        }
    }
}
/**
 * Query parameters for the org extensions DataTable API call (frontend → backend).
 * Accepts a pre-parsed filters array — not bracket notation.
 *
 * Note: Defaults are NOT set here. The handler enforces defaults (name asc).
 */
export const listOrgExtensionsDataTableParamsSchema = z
    .object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    filters: FiltersSchema.optional(),
    sortBy: orgExtensionSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    knownTotal: knownTotalParamsSchema,
})
    .superRefine((data, ctx) => validateOrgExtensionFilters(data.filters, ctx));
/**
 * Backend query schema for org extensions DataTable.
 * Handles bracket-notation filter params from Fastify OR a pre-parsed filters array.
 */
const orgExtensionDataTableQueryBaseSchema = paginationSchema.extend({
    sortBy: orgExtensionSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    knownTotal: knownTotalQuerySchema,
});
export const orgExtensionDataTableQuerySchema = createTableQuerySchema(orgExtensionDataTableQueryBaseSchema, ORG_EXTENSION_COLUMN_DATA_TYPES, ORG_EXTENSION_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    validateOrgExtensionFilters(data.filters, ctx);
});
/**
 * Response schema for the org extensions DataTable endpoint.
 * Shape: { data: ExtensionListItem[], pagination: {...}, meta: {...} }
 */
export const orgExtensionTableResponseSchema = z.object({
    data: z.array(extensionListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
/**
 * Fields with server-backed dynamic filter options for org extensions.
 * Extends personal set with USER field.
 *
 * - name, publisher: Text search with suggestions
 * - user: User email/name search for filtering
 * - source, ide: Static enum options (no server fetch needed)
 * - firstSeenAt: Date picker (no server fetch needed)
 */
export const orgExtensionFilterOptionFieldSchema = z.enum([
    ORG_EXTENSION_FILTERABLE_FIELDS.NAME,
    ORG_EXTENSION_FILTERABLE_FIELDS.PUBLISHER,
    ORG_EXTENSION_FILTERABLE_FIELDS.USER,
    ORG_EXTENSION_FILTERABLE_FIELDS.IDE,
    ORG_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE,
    ORG_EXTENSION_FILTERABLE_FIELDS.SCAN_STATUS,
]);
/**
 * Query schema for fetching org extension filter options.
 */
export const orgExtensionFilterOptionsQuerySchema = z.object({
    field: orgExtensionFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/**
 * Response schema for org extension filter options.
 * For 'user' field, returns rich objects with value/label/metadata.
 * For other fields ('name', 'publisher'), returns simple strings.
 */
export const orgExtensionFilterOptionsResponseSchema = z.object({
    field: orgExtensionFilterOptionFieldSchema,
    options: z.union([z.array(z.string()), z.array(filterOptionItemSchema)]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
// Re-export FilterOptionItem for rich user filter options
export { filterOptionItemSchema };
//# sourceMappingURL=extensions.js.map